import React, { useState } from 'react';
import { submitContactForm } from '../services/strapi';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  company: '',
  message: '',
  packageInterest: '',
};

const INITIAL_ERRORS = {};

export default function ContactForm({ preselectedPackage = '' }) {
  const [formData, setFormData] = useState({
    ...INITIAL_FORM,
    packageInterest: preselectedPackage,
  });
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [serverError, setServerError] = useState('');

  const validate = () => {
    const newErrors = {};

    // Name
    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrævet';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Navn skal være mindst 2 tegn';
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ugyldig e-mail adresse';
    }

    // Phone (optional but validate format if provided)
    if (formData.phone.trim() && !/^[+]?[\d\s\-()]{6,20}$/.test(formData.phone)) {
      newErrors.phone = 'Ugyldigt telefonnummer';
    }

    // Message
    if (!formData.message.trim()) {
      newErrors.message = 'Besked er påkrævet';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Beskeden skal være mindst 10 tegn';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validate()) return;

    setStatus('submitting');

    try {
      await submitContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        company: formData.company.trim() || null,
        message: formData.message.trim(),
        packageInterest: formData.packageInterest || null,
      });

      setStatus('success');
      setFormData({ ...INITIAL_FORM });
    } catch (err) {
      setStatus('error');
      if (err.response?.data?.error?.message) {
        setServerError(err.response.data.error.message);
      } else {
        setServerError('Der opstod en fejl. Prøv venligst igen eller kontakt os på info@sikker-smv.dk');
      }
    }
  };

  if (status === 'success') {
    return (
      <div className="contact-success">
        <div className="contact-success__icon">✅</div>
        <h3 className="contact-success__title">Tak for din henvendelse!</h3>
        <p className="contact-success__text">
          Vi har modtaget din besked og vender tilbage til dig hurtigst muligt – typisk inden for 1-2 hverdage.
        </p>
        <button
          className="btn btn--outline"
          onClick={() => setStatus('idle')}
          style={{ marginTop: '1rem' }}
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      {status === 'error' && serverError && (
        <div className="contact-form__error-banner">
          ⚠️ {serverError}
        </div>
      )}

      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="contact-name">Navn *</label>
          <input
            id="contact-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className={errors.name ? 'input--error' : ''}
            autoComplete="name"
          />
          {errors.name && <span className="contact-form__error">{errors.name}</span>}
        </div>

        <div className="contact-form__field">
          <label htmlFor="contact-email">E-mail *</label>
          <input
            id="contact-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="din@email.dk"
            className={errors.email ? 'input--error' : ''}
            autoComplete="email"
          />
          {errors.email && <span className="contact-form__error">{errors.email}</span>}
        </div>
      </div>

      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="contact-phone">Telefon</label>
          <input
            id="contact-phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+45 12 34 56 78"
            className={errors.phone ? 'input--error' : ''}
            autoComplete="tel"
          />
          {errors.phone && <span className="contact-form__error">{errors.phone}</span>}
        </div>

        <div className="contact-form__field">
          <label htmlFor="contact-company">Virksomhed</label>
          <input
            id="contact-company"
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Din virksomheds navn"
            autoComplete="organization"
          />
        </div>
      </div>

      {formData.packageInterest && (
        <div className="contact-form__field">
          <label>Interesseret i pakke</label>
          <div className="contact-form__package-badge">
            📦 {formData.packageInterest.charAt(0).toUpperCase() + formData.packageInterest.slice(1)}
            <button
              type="button"
              className="contact-form__package-clear"
              onClick={() => setFormData((prev) => ({ ...prev, packageInterest: '' }))}
              aria-label="Fjern pakkevalg"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="contact-form__field">
        <label htmlFor="contact-message">Besked *</label>
        <textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Fortæl os hvad vi kan hjælpe dig med..."
          rows={5}
          className={errors.message ? 'input--error' : ''}
        />
        {errors.message && <span className="contact-form__error">{errors.message}</span>}
      </div>

      <button
        type="submit"
        className="btn btn--primary contact-form__submit"
        disabled={status === 'submitting'}
      >
        {status === 'submitting' ? (
          <>
            <span className="contact-form__spinner"></span>
            Sender...
          </>
        ) : (
          'Send besked'
        )}
      </button>

      <p className="contact-form__privacy">
        🔒 Dine oplysninger behandles fortroligt i henhold til vores privatlivspolitik.
      </p>
    </form>
  );
}
